import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import {
  Target,
  CurrencyDollar,
  MagnifyingGlass,
  EnvelopeSimple,
  Phone,
  CaretRight,
} from "@phosphor-icons/react";

const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AdminScenarios() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/admin/scenarios")
      .then((r) => {
        setData(r.data);
        if (r.data.users?.length) setSelected(r.data.users[0]);
      })
      .catch(() => toast.error("Failed to load scenarios"));
  }, []);

  const filtered = useMemo(() => {
    const users = data?.users || [];
    if (!q.trim()) return users;
    const needle = q.toLowerCase();
    return users.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(needle) ||
        (u.name || "").toLowerCase().includes(needle) ||
        u.scenarios.some((s) =>
          (s.label || "").toLowerCase().includes(needle) ||
          (s.goal_type || "").toLowerCase().includes(needle),
        ),
    );
  }, [q, data]);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="admin-scenarios-page">
        <div className="mb-8">
          <p className="eyebrow"><span className="gold-divider" />Admin · Goal Lab Pulse</p>
          <h1 className="font-display text-4xl mt-3">Every user&apos;s saved scenarios</h1>
          <p className="text-sm text-exodus-muted mt-2 max-w-2xl">
            What your prospects actually want, the numbers they&apos;re playing with, the strategies
            they chose. Bring this into every booked call — they&apos;ve already done half the work.
          </p>
        </div>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <Kpi label="Total scenarios" value={data.total_scenarios} />
            <Kpi label="Users with goals" value={data.users.length} />
            <Kpi label="Avg per user" value={data.users.length ? (data.total_scenarios / data.users.length).toFixed(1) : "0"} />
            <Kpi label="Top goal" value={topGoal(data.users)} />
          </div>
        )}

        <div className="card-surface flex items-center gap-3 px-4 py-2 mb-6">
          <MagnifyingGlass size={16} className="text-exodus-muted" />
          <input
            data-testid="admin-scenario-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, goal, or scenario label…"
            className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-exodus-muted/70"
          />
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* LEFT — user list */}
          <div className="lg:col-span-4 card-surface divide-y divide-exodus-border max-h-[70vh] overflow-auto">
            {filtered.length === 0 ? (
              <p className="p-6 text-exodus-muted text-sm">No users have saved a scenario yet.</p>
            ) : (
              filtered.map((u) => (
                <button
                  key={u.email}
                  data-testid={`admin-scenario-user-${u.email}`}
                  onClick={() => setSelected(u)}
                  className={`w-full px-5 py-4 flex items-center gap-3 text-left transition-colors ${
                    selected?.email === u.email
                      ? "bg-exodus-elevated border-l-2 border-exodus-gold"
                      : "hover:bg-exodus-elevated"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base truncate">{u.name || u.email}</p>
                    <p className="text-xs text-exodus-muted truncate">{u.email}</p>
                    <p className="text-[10px] gold-text mt-1">
                      {u.scenario_count} scenario{u.scenario_count === 1 ? "" : "s"}
                      {u.quiz_recommendation?.primary_label
                        ? ` · ${u.quiz_recommendation.primary_label}`
                        : ""}
                    </p>
                  </div>
                  <CaretRight size={14} className="text-exodus-muted shrink-0" />
                </button>
              ))
            )}
          </div>

          {/* RIGHT — scenarios */}
          <div className="lg:col-span-8 space-y-4">
            {selected ? (
              <>
                <div className="card-surface p-6">
                  <p className="eyebrow text-xs">User</p>
                  <h2 className="font-display text-2xl mt-1">{selected.name || "—"}</h2>
                  <div className="flex flex-wrap gap-4 mt-2 text-xs text-exodus-muted">
                    <span><EnvelopeSimple size={12} className="inline mr-1" />{selected.email}</span>
                    {selected.phone && <span><Phone size={12} className="inline mr-1" />{selected.phone}</span>}
                    {selected.quiz_recommendation?.primary_label && (
                      <span>★ Quiz: <span className="gold-text">{selected.quiz_recommendation.primary_label}</span></span>
                    )}
                  </div>
                </div>

                {selected.scenarios.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`scenario-${s.id}`}
                    className="card-surface p-5"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="eyebrow text-[10px]">{s.goal_type.replace(/_/g, " ")}</p>
                        <h3 className="font-display text-xl mt-1">{s.label}</h3>
                        <p className="text-xs text-exodus-muted mt-1">{fmtDate(s.created_at)}</p>
                      </div>
                      <span className="text-xs gold-text font-mono uppercase">{s.product}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                      <MicroStat label="Age" v={`${s.current_age} → ${s.target_age}`} />
                      <MicroStat label="Monthly" v={fmt(s.monthly_contribution || 0)} />
                      <MicroStat label="Lump sum" v={fmt(s.lump_sum || 0)} />
                      <MicroStat label="Target" v={fmt(s.target_amount || 0)} />
                    </div>
                    <div className="mt-4 pt-4 border-t border-exodus-border grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[10px] eyebrow text-exodus-muted">Projected at target age</p>
                        <p className="font-display text-xl gold-text mt-1">{fmt(s.result?.projected_value)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] eyebrow text-exodus-muted">Annual income / DB est.</p>
                        <p className="font-display text-xl mt-1">
                          {fmt(s.result?.income_phase?.annual_tax_free_income || s.result?.income_phase?.annual_lifetime_income || 0)}
                          {" / "}
                          {fmt(s.result?.death_benefit_estimate)}
                        </p>
                      </div>
                    </div>
                    {s.notes && (
                      <p className="text-xs text-exodus-muted mt-3 italic">&ldquo;{s.notes}&rdquo;</p>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <div className="card-surface p-10 text-center text-exodus-muted text-sm">
                Select a user.
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="card-surface p-4">
      <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
      <p className="font-display text-2xl mt-1">{value}</p>
    </div>
  );
}

function MicroStat({ label, v }) {
  return (
    <div>
      <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
      <p className="text-sm font-display mt-1">{v}</p>
    </div>
  );
}

function topGoal(users) {
  const tally = {};
  users.forEach((u) => u.scenarios.forEach((s) => {
    tally[s.goal_type] = (tally[s.goal_type] || 0) + 1;
  }));
  const sorted = Object.entries(tally).sort((a, b) => b[1] - a[1]);
  if (!sorted.length) return "—";
  return sorted[0][0].replace(/_/g, " ");
}
